import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { quoteService } from '@/lib/api/quote.service';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import { handleApiError } from '@/lib/api/client';
import type { NegotiationRound, Quote } from '@/types/quote.types';

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
};

const formatDate = (value: Date | null | string): string => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const formatDateTime = (value: Date | null | string): string => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

export const CelebrationQuoteDetail: React.FC = () => {
  const { celebrationId, quoteId } = useParams<{ celebrationId: string; quoteId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['quotes', 'common']);

  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [roundsLoading, setRoundsLoading] = useState<boolean>(false);
  const [roundActionLoading, setRoundActionLoading] = useState<boolean>(false);
  const [rounds, setRounds] = useState<NegotiationRound[]>([]);
  const [requestServiceId, setRequestServiceId] = useState<number | null>(null);

  const [showRejectForm, setShowRejectForm] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [showRoundForm, setShowRoundForm] = useState<boolean>(false);
  const [roundMessage, setRoundMessage] = useState<string>('');
  const [roundAmount, setRoundAmount] = useState<string>('');
  const [roundCurrency, setRoundCurrency] = useState<string>('');

  const [responseNote, setResponseNote] = useState<string>('');
  const [counterMessage, setCounterMessage] = useState<string>('');
  const [counterAmount, setCounterAmount] = useState<string>('');
  const [counterCurrency, setCounterCurrency] = useState<string>('');

  const celebrationIdNum = Number.parseInt(celebrationId || '', 10);
  const quoteIdNum = Number.parseInt(quoteId || '', 10);

  const loadQuote = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await quoteService.getQuote(celebrationIdNum, quoteIdNum);
      setQuote(data);
      if (data.status === 'submitted') {
        await quoteService.markViewed(celebrationIdNum, quoteIdNum);
        setQuote((prev) => (prev ? { ...prev, status: 'viewed' } : prev));
      }
    } catch (err: unknown) {
      const msg = handleApiError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadRounds = async () => {
    setRoundsLoading(true);
    try {
      const data = await quoteService.listNegotiationRounds(celebrationIdNum, quoteIdNum);
      setRounds(data);
    } catch {
      setRounds([]);
    } finally {
      setRoundsLoading(false);
    }
  };

  useEffect(() => {
    if (
      !Number.isFinite(celebrationIdNum) ||
      celebrationIdNum <= 0 ||
      !Number.isFinite(quoteIdNum) ||
      quoteIdNum <= 0
    ) {
      setError(t('quotes:detail.errors.invalidId'));
      return;
    }
    loadQuote();
    loadRounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrationIdNum, quoteIdNum]);

  useEffect(() => {
    const loadRequestContext = async () => {
      if (!quote?.request_id || !Number.isFinite(celebrationIdNum) || celebrationIdNum <= 0) {
        setRequestServiceId(null);
        return;
      }

      try {
        const request = await serviceRequestApiService.getRequest(celebrationIdNum, quote.request_id);
        setRequestServiceId(request.service_id);
      } catch {
        setRequestServiceId(null);
      }
    };

    void loadRequestContext();
  }, [celebrationIdNum, quote?.request_id]);

  const refreshRoundData = async () => {
    await Promise.all([loadQuote(), loadRounds()]);
  };

  const handleAccept = async () => {
    if (!quote) return;
    setActionLoading(true);
    setError(null);
    try {
      const updated = await quoteService.acceptQuote(celebrationIdNum, quoteIdNum);
      setQuote(updated);
    } catch (err: unknown) {
      const msg = handleApiError(err);
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateRound = async () => {
    if (!quote) return;

    const parsedAmount = roundAmount.trim() ? Number(roundAmount) : undefined;
    if (parsedAmount !== undefined && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
      setError(t('quotes:detail.errors.invalidAmount'));
      return;
    }

    setRoundActionLoading(true);
    setError(null);
    try {
      await quoteService.createNegotiationRound(celebrationIdNum, quoteIdNum, {
        message: roundMessage.trim() || undefined,
        proposed_total_amount: parsedAmount,
        currency: roundCurrency.trim() || undefined,
      });
      setShowRoundForm(false);
      setRoundMessage('');
      setRoundAmount('');
      setRoundCurrency('');
      await refreshRoundData();
    } catch (err: unknown) {
      const msg = handleApiError(err);
      setError(msg);
    } finally {
      setRoundActionLoading(false);
    }
  };

  const handleRespondRound = async (roundId: number, action: 'accept' | 'reject' | 'counter') => {
    const parsedCounterAmount = counterAmount.trim() ? Number(counterAmount) : undefined;
    if (
      parsedCounterAmount !== undefined
      && (!Number.isFinite(parsedCounterAmount) || parsedCounterAmount <= 0)
    ) {
      setError(t('quotes:detail.errors.invalidCounterAmount'));
      return;
    }

    setRoundActionLoading(true);
    setError(null);
    try {
      await quoteService.respondNegotiationRound(celebrationIdNum, quoteIdNum, roundId, {
        action,
        response_note: responseNote.trim() || undefined,
        counter_message: action === 'counter' ? counterMessage.trim() || undefined : undefined,
        counter_proposed_total_amount: action === 'counter' ? parsedCounterAmount : undefined,
        counter_currency: action === 'counter' ? counterCurrency.trim() || undefined : undefined,
      });
      setResponseNote('');
      setCounterMessage('');
      setCounterAmount('');
      setCounterCurrency('');
      await refreshRoundData();
    } catch (err: unknown) {
      const msg = handleApiError(err);
      setError(msg);
    } finally {
      setRoundActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!quote) return;
    setActionLoading(true);
    setError(null);
    try {
      const updated = await quoteService.rejectQuote(
        celebrationIdNum,
        quoteIdNum,
        rejectReason || undefined,
      );
      setQuote(updated);
      setShowRejectForm(false);
      setRejectReason('');
    } catch (err: unknown) {
      const msg = handleApiError(err);
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const canActOnQuote = quote?.status === 'submitted' || quote?.status === 'viewed';
  const openRound = rounds.find((round) => round.status === 'open');

  return (
    <>
      <AppBreadcrumbs
        items={[
          { label: 'Celebrations', to: '/celebrations' },
          { label: 'Celebration', to: `/celebrations/${celebrationId}` },
          { label: t('common:nav.quotes'), to: `/celebrations/${celebrationId}/quotes` },
          { label: quote?.quote_number ?? t('quotes:detail.fallbackTitle') },
        ]}
      />
      <PageContainer>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {!loading && error && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}

        {!loading && quote && (
          <div className="space-y-6">
            {/* Header card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('quotes:detail.title', { number: quote.quote_number })}</CardTitle>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                      quote.status === 'accepted'
                        ? 'bg-green-100 text-green-800'
                        : quote.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : quote.status === 'submitted' || quote.status === 'viewed'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {quote.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.subtotal')}</p>
                    <p className="font-medium">
                      {formatCurrency(quote.subtotal, quote.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.discount')}</p>
                    <p className="font-medium">
                      {formatCurrency(quote.discount_amount, quote.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.tax')}</p>
                    <p className="font-medium">
                      {formatCurrency(quote.tax_amount, quote.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.total')}</p>
                    <p className="text-lg font-bold">
                      {formatCurrency(quote.total_amount, quote.currency)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.validUntil')}</p>
                    <p>{formatDate(quote.valid_until)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.vendorId')}</p>
                    <p>{quote.vendor_id}</p>
                  </div>
                </div>

                {quote.notes && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.notes')}</p>
                    <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
                  </div>
                )}

                {quote.terms && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quotes:detail.fields.terms')}</p>
                    <p className="text-sm whitespace-pre-wrap">{quote.terms}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Items card */}
            {Array.isArray(quote.items) && quote.items.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('quotes:detail.fields.items')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs bg-muted rounded p-3 overflow-auto">
                    {JSON.stringify(quote.items, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}

            {/* Accepted info */}
            {quote.status === 'accepted' && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-green-700">
                    {t('quotes:detail.status.acceptedOn', { date: formatDateTime(quote.accepted_at) })}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Rejected info */}
            {quote.status === 'rejected' && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-red-700">
                    {t('quotes:detail.status.rejectedOn', { date: formatDateTime(quote.rejected_at) })}
                  </p>
                  {quote.rejection_reason && (
                    <p className="text-sm mt-1">
                      <span className="font-medium">{t('quotes:detail.status.reason')}</span>
                      {quote.rejection_reason}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/celebrations/${celebrationId}/quotes`)}
              >
                {t('quotes:detail.buttons.backToQuotes')}
              </Button>

              {quote.status === 'accepted' && requestServiceId && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(
                      `/celebrations/${celebrationId}/services/${requestServiceId}/requests/${quote.request_id}/vendors/${quote.vendor_id}/finalize`,
                    )
                  }
                >
                  {t('quotes:detail.buttons.continueFinalization')}
                </Button>
              )}

              {canActOnQuote && !showRejectForm && (
                <>
                  <Button
                    onClick={handleAccept}
                    disabled={actionLoading}
                  >
                    {actionLoading ? t('quotes:detail.buttons.accepting') : t('quotes:detail.buttons.acceptQuote')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setShowRejectForm(true)}
                    disabled={actionLoading}
                  >
                    {t('quotes:detail.buttons.rejectQuote')}
                  </Button>
                </>
              )}
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>{t('quotes:detail.rounds.title')}</CardTitle>
                  {canActOnQuote && !openRound && !showRoundForm && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowRoundForm(true)}
                      disabled={roundActionLoading}
                    >
                      {t('quotes:detail.rounds.openRound')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {roundsLoading && <p className="text-sm text-muted-foreground">{t('quotes:detail.rounds.loading')}</p>}

                {!roundsLoading && rounds.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t('quotes:detail.rounds.empty')}</p>
                )}

                {showRoundForm && (
                  <div className="space-y-3 rounded-md border p-3">
                    <p className="text-sm font-medium">{t('quotes:detail.rounds.createTitle')}</p>
                    <Textarea
                      value={roundMessage}
                      onChange={(e) => setRoundMessage(e.target.value)}
                      placeholder={t('quotes:detail.rounds.messagePlaceholder')}
                    />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <input
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        value={roundAmount}
                        onChange={(e) => setRoundAmount(e.target.value)}
                        placeholder={t('quotes:detail.rounds.amountPlaceholder')}
                      />
                      <input
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        value={roundCurrency}
                        onChange={(e) => setRoundCurrency(e.target.value)}
                        placeholder={t('quotes:detail.rounds.currencyPlaceholder')}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleCreateRound}
                        disabled={roundActionLoading}
                      >
                        {roundActionLoading ? t('quotes:detail.rounds.saving') : t('quotes:detail.rounds.create')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowRoundForm(false)}
                        disabled={roundActionLoading}
                      >
                        {t('quotes:detail.rounds.cancel')}
                      </Button>
                    </div>
                  </div>
                )}

                {!roundsLoading && rounds.length > 0 && (
                  <div className="space-y-3">
                    {rounds.map((round) => (
                      <div key={round.id} className="rounded-md border p-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-medium">{t('quotes:detail.rounds.roundTitle', { id: round.id })}</p>
                          <span className="text-xs rounded-full bg-muted px-2 py-0.5 uppercase tracking-wide">
                            {round.status}
                          </span>
                        </div>
                        {round.message && <p className="text-sm whitespace-pre-wrap">{round.message}</p>}
                        {(round.proposed_total_amount !== null && round.currency) && (
                          <p className="text-sm text-muted-foreground">
                            {t('quotes:detail.rounds.proposedAmount', { amount: formatCurrency(round.proposed_total_amount, round.currency) })}
                          </p>
                        )}
                        {round.response_note && (
                          <p className="text-sm text-muted-foreground">{t('quotes:detail.rounds.response', { note: round.response_note })}</p>
                        )}
                        {round.linked_quote_id && (
                          <p className="text-xs text-muted-foreground">
                            {t('quotes:detail.rounds.linkedQuote', { id: round.linked_quote_id })}
                          </p>
                        )}

                        {round.status === 'open' && canActOnQuote && (
                          <div className="space-y-2 pt-2 border-t">
                            <Textarea
                              value={responseNote}
                              onChange={(e) => setResponseNote(e.target.value)}
                              placeholder={t('quotes:detail.rounds.responseNotePlaceholder')}
                            />
                            <Textarea
                              value={counterMessage}
                              onChange={(e) => setCounterMessage(e.target.value)}
                              placeholder={t('quotes:detail.rounds.counterMessagePlaceholder')}
                            />
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <input
                                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                                value={counterAmount}
                                onChange={(e) => setCounterAmount(e.target.value)}
                                placeholder={t('quotes:detail.rounds.counterAmountPlaceholder')}
                              />
                              <input
                                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                                value={counterCurrency}
                                onChange={(e) => setCounterCurrency(e.target.value)}
                                placeholder={t('quotes:detail.rounds.counterCurrencyPlaceholder')}
                              />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleRespondRound(round.id, 'accept')}
                                disabled={roundActionLoading}
                              >
                                {t('quotes:detail.rounds.acceptRound')}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleRespondRound(round.id, 'reject')}
                                disabled={roundActionLoading}
                              >
                                {t('quotes:detail.rounds.rejectRound')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRespondRound(round.id, 'counter')}
                                disabled={roundActionLoading}
                              >
                                {t('quotes:detail.rounds.counterRound')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reject inline form */}
            {canActOnQuote && showRejectForm && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('quotes:detail.rejectForm.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label
                      htmlFor="reject-reason"
                      className="block text-sm font-medium mb-1"
                    >
                      {t('quotes:detail.rejectForm.reasonLabel')}
                    </label>
                    <textarea
                      id="reject-reason"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      rows={3}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Provide a reason for rejection…"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="destructive"
                      onClick={handleReject}
                      disabled={actionLoading}
                    >
                      {actionLoading ? t('quotes:detail.rejectForm.rejecting') : t('quotes:detail.rejectForm.confirm')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowRejectForm(false);
                        setRejectReason('');
                      }}
                      disabled={actionLoading}
                    >
                      {t('common:actions.cancel')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </PageContainer>
    </>
  );
};
